import { FC, memo, useMemo } from "react";
import { TagMetaData } from "src/config/store/services/media-api/types";
import { getTagPosition } from "../../use-media-player";
import { TagMarker, TagMarkerItem } from "../tag-marker/tag-marker";

type Props = {
  tags: TagMetaData[];
  video: HTMLVideoElement;
  onClick: (time: number) => void;
};

export const TagMarkers: FC<Props> = memo(({ tags, video, onClick }) => {
  const markers: TagMarkerItem[] = useMemo(
    () =>
      tags
        .flatMap((tag) => {
          if (!tag.data) {
            return undefined;
          }

          const visibilityTime = tag.nodeMeasures.find((measure) => {
            const { isVisible } = getTagPosition(video, measure);

            return isVisible;
          })?.time;

          if (!visibilityTime || visibilityTime < 1000) {
            return [];
          }

          const position =
            ((visibilityTime || 0) / (video.duration * 1000)) * 100;

          return {
            id: tag.id,
            names: [tag.data.name.toUpperCase()],
            position: Math.round(position * 10) / 10,
            color: tag.data.color,
            icons: [tag.data.icon],
          };
        })
        .filter((x): x is TagMarkerItem => !!x),
    [tags, video]
  );

  const result: TagMarkerItem[] = useMemo(
    () =>
      Object.values(
        markers.reduce((prev, current) => {
          prev[current.position] ??= { ...current, names: [], icons: [] };

          prev[current.position].names = prev[current.position].names.concat(
            current.names
          );

          prev[current.position].icons = prev[current.position].icons.concat(
            current.icons
          );

          return prev;
        }, {} as { [key: number]: TagMarkerItem })
      ),
    [markers]
  );

  return (
    <>
      {result.map((marker) => (
        <TagMarker
          key={marker.id}
          marker={marker}
          handleVideoProgress={onClick}
        />
      ))}
    </>
  );
});
